import { api } from '@/config/api';

export interface Contact {
  _id: string; contactUid: string; companyUid: string;
  firstName: string; lastName?: string; email?: string; phone: string; countryCode: string;
  whatsappPhone?: string; company?: string; jobTitle?: string; city?: string; country?: string; notes?: string;
  tags: string[]; customFields: Record<string, string>;
  optedIn: boolean;
  whatsappConsentAt?: string; whatsappConsentPhone?: string; whatsappConsentNote?: string; optedOutAt?: string;
  /** Channel-specific opt-out: contact has consented to in-CRM communication
   *  but explicitly opted out of having their PII shared with Meta for Custom
   *  Audience matching. GDPR/CCPA-relevant. Checked by the audiences sync
   *  before hashing + uploading. Defaults to false. */
  metaShareOptedOut?: boolean;
  /** List endpoint: the contact's groups, sorted by name. */
  groups?: ContactGroupChip[];
  createdAt: string; updatedAt: string;
}

export interface ContactGroupChip { groupUid: string; name: string; color: string; }

export interface ListResponse { items: Contact[]; total: number; page: number; limit: number; }

export type CustomerSection = 'leads' | 'conversations' | 'deals' | 'tasks' | 'notes' | 'bookings' | 'orders' | 'documents' | 'activity';
export interface CustomerProfileRow {
  id: string; title?: string; body?: string; summary?: string; at?: string; status?: string; source?: string;
  sourceCampaignId?: string; sourceAdId?: string; sourceFormId?: string; phone?: string; email?: string;
  accountName?: string; accountPhone?: string; accountUid?: string; direction?: string; mediaType?: string; mediaUrl?: string;
  amount?: number; currency?: string; stageName?: string; expectedCloseDate?: string; priority?: string; dueAt?: string;
  bookingDate?: string; slotStart?: string; slotEnd?: string; timezone?: string; number?: number; type?: string;
  paymentStatus?: string; kind?: string; entityType?: string; entityUid?: string;
}
export interface CustomerProfileSection { enabled: boolean; rows: CustomerProfileRow[]; total: number; page: number; pageSize: number; }
export interface CustomerProfile { contactUid: string; asOf: string; sections: Partial<Record<CustomerSection, CustomerProfileSection>>; }

export const contactsService = {
  list: async (q: { q?: string; groupUid?: string; page?: number; limit?: number } = {}): Promise<ListResponse> =>
    (await api.get('/slighter_crm/contacts', { params: q })).data,
  get: async (uid: string): Promise<Contact & { groups: any[] }> =>
    (await api.get(`/slighter_crm/contacts/${uid}`)).data,
  profile: async (uid: string, params: { section?: CustomerSection; page?: number } = {}): Promise<CustomerProfile> =>
    (await api.get(`/slighter_crm/contacts/${uid}/profile`, { params })).data,
  create: async (p: Partial<Contact>): Promise<Contact> =>
    (await api.post('/slighter_crm/contacts', p)).data,
  update: async (uid: string, p: Partial<Contact>): Promise<Contact> =>
    (await api.patch(`/slighter_crm/contacts/${uid}`, p)).data,
  setWhatsappConsent: async (uid: string, granted: boolean, note: string): Promise<Contact> =>
    (await api.post(`/slighter_crm/contacts/${uid}/whatsapp-consent`, { granted, note })).data,
  remove: async (uid: string) => { await api.delete(`/slighter_crm/contacts/${uid}`); },
  bulkDelete: async (contactUids: string[]): Promise<{ deleted: number; requested: number }> =>
    (await api.post('/slighter_crm/contacts/bulk-delete', { contactUids })).data,
  bulkAddToGroup: async (contactUids: string[], groupUid: string) =>
    (await api.post('/slighter_crm/contacts/bulk-add-to-group', { contactUids, groupUid })).data,
  bulkRemoveFromGroup: async (contactUids: string[], groupUid: string) =>
    (await api.post('/slighter_crm/contacts/bulk-remove-from-group', { contactUids, groupUid })).data,
  bulkAddTags: async (contactUids: string[], tags: string[]): Promise<{ updated: number }> =>
    (await api.post('/slighter_crm/contacts/bulk-add-tags', { contactUids, tags })).data,
  bulkRemoveTags: async (contactUids: string[], tags: string[]): Promise<{ updated: number }> =>
    (await api.post('/slighter_crm/contacts/bulk-remove-tags', { contactUids, tags })).data,
  /** Bulk-toggle the Meta-share opt-out flag for many contacts.
   *  When `optedOut=true`, each contact is also pushed through the GDPR
   *  delete-propagation pipeline so Meta removes their PII from every
   *  Custom Audience. `propagated` = number of GDPR events fired. */
  bulkSetMetaShareOptedOut: async (
    contactUids: string[],
    optedOut: boolean,
  ): Promise<{ updated: number; propagated: number }> =>
    (await api.post('/slighter_crm/contacts/bulk-set-meta-share-opted-out', { contactUids, optedOut })).data,
  importCsv: async (payload: { filename: string; mapping: Record<string, string>; rows: Record<string, string>[]; defaultCountryCode?: string }) =>
    (await api.post('/slighter_crm/contacts/import', payload)).data as { importUid: string },
  importStatus: async (importUid: string) =>
    (await api.get(`/slighter_crm/contacts/imports/${importUid}`)).data,

  // ── Duplicate detect + merge ─────────────────────────────────────────
  duplicates: async (limit = 100): Promise<DuplicateCluster[]> =>
    (await api.get('/slighter_crm/contacts/duplicates', { params: { limit } })).data,
  merge: async (primaryUid: string, otherUids: string[]): Promise<MergeResult> =>
    (await api.post('/slighter_crm/contacts/merge', { primaryUid, otherUids })).data,

  // ── Per-contact email thread (outbound + inbound) ────────────────────
  emails: async (contactUid: string): Promise<ContactEmailThread> =>
    (await api.get(`/slighter_crm/contacts/${contactUid}/emails`)).data,
};

export interface ContactEmailMessage {
  messageUid?: string;
  eventUid?: string;
  direction: 'inbound' | 'outbound';
  toEmail?: string;
  fromEmail?: string;
  from?: string;
  subject?: string;
  bodyHtml?: string;
  snippet?: string;
  status?: string;
  sentAt?: string;
  openedAt?: string;
  clickedAt?: string;
  openCount?: number;
  clickCount?: number;
  createdAt: string;
}

export interface ContactEmailThread {
  outbound: ContactEmailMessage[];
  inbound: ContactEmailMessage[];
}

export interface DuplicateClusterContact {
  contactUid: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  createdAt: string;
  leadCount?: number;
  dealCount?: number;
  membershipCount?: number;
}

export interface DuplicateCluster {
  matchKey: string;
  matchKind: 'phone' | 'email';
  contacts: DuplicateClusterContact[];
}

export interface MergeResult {
  primaryUid: string;
  mergedCount: number;
  moved: { leads: number; deals: number; memberships: number; documents: number; events: number; customRecords: number };
}
